import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { KeyRound, Lock, Loader2 } from 'lucide-react';
import { usePasswordReveal } from '../components/PasswordReveal';
import { useAuth } from '../context/AuthContext';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token') || '';
  const welcome = new URLSearchParams(location.search).get('welcome') === '1';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwType, pwEye] = usePasswordReveal('reset-password-reveal');
  const [cfType, cfEye] = usePasswordReveal('reset-confirm-reveal');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/account'), 1200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 fade-in">
      <div className="bg-white rounded-lg shadow-md p-8 border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <div className="text-center mb-6">
          <div className="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-full" style={{ background: 'var(--navy)' }}>
            <KeyRound size={24} color="var(--cream)" />
          </div>
          <h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }} data-testid="reset-title">{welcome ? 'Choose a password to update your details' : 'Choose a new password'}</h1>
          {welcome && <p className="text-sm mt-2" style={{ color: 'var(--olive)' }} data-testid="reset-welcome">Once signed in you can review your contact details and how we keep in touch.</p>}
        </div>

        {!token && (
          <p className="text-sm text-center px-3 py-2 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid="reset-no-token">
            This reset link is missing its token. Please use the link from your email, or{' '}
            <Link to="/login" className="underline">request a new one</Link>.
          </p>
        )}

        {token && done && (
          <p className="text-sm text-center px-3 py-2 rounded" style={{ background: '#eaf6ec', color: '#1c6b2c' }} data-testid="reset-success">
            Password updated. Signing you in…
          </p>
        )}

        {token && !done && (
          <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
            {error && (
              <p className="text-sm text-center px-3 py-2 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid="reset-error">{error}</p>
            )}
            <div className="flex items-center gap-2 border rounded px-3 py-2.5" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <Lock size={16} style={{ color: 'var(--olive)' }} />
              <input type={pwType} required minLength={8} className="w-full outline-none text-sm" placeholder="New password (min 8 characters)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
              {pwEye}
            </div>
            <div className="flex items-center gap-2 border rounded px-3 py-2.5" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <Lock size={16} style={{ color: 'var(--olive)' }} />
              <input type={cfType} required minLength={8} className="w-full outline-none text-sm" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
              {cfEye}
            </div>
            <button type="submit" disabled={busy} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded font-semibold" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }} data-testid="reset-submit">
              {busy && <Loader2 size={16} className="animate-spin" />} Update password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
